/**
 * Validates in-app return paths against open-redirect vectors that survive URL parsing
 * (protocol-relative pathnames, backslash normalization, nested percent-encoding).
 * Mirrors backend onboarding.SanitizeInternalReturnURL checks.
 */

export function rawPathContainsEncodedSeparators(rawPath: string): boolean {
  const lower = rawPath.toLowerCase();
  return lower.includes('%2f') || lower.includes('%5c');
}

export function isSafeInternalPath(path: string): boolean {
  if (!path || !path.startsWith('/') || /[\r\n]/.test(path)) {
    return false;
  }

  let current = path;
  while (true) {
    if (current.startsWith('//') || current.includes('\\') || /[\r\n]/.test(current)) {
      return false;
    }

    try {
      const decoded = decodeURIComponent(current);
      if (decoded === current) {
        return true;
      }
      current = decoded;
    } catch {
      return true;
    }
  }
}

/** Validates a client return URL before extracting pathname for React Router navigation. */
export function isSafeInternalReturnPath(redirectPath: string): boolean {
  const destination = redirectPath.trim();
  if (!destination) {
    return false;
  }

  // WHATWG URL parsers normalize '\' to '/', so '/\evil.com' becomes '//evil.com'.
  if (destination.includes('\\')) {
    return false;
  }

  if (destination.startsWith('/')) {
    if (destination.startsWith('//')) {
      return false;
    }

    const rawPath = destination.split('?')[0].split('#')[0];
    if (rawPathContainsEncodedSeparators(rawPath)) {
      return false;
    }
  }

  let url: URL;
  try {
    url = new URL(destination, window.location.origin);
  } catch {
    return false;
  }

  if (url.host !== window.location.host) {
    return false;
  }

  return isSafeInternalPath(url.pathname);
}
